import { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { headers, renderRow, Row, renderCondensedRow, ActivityInstance } from './content';
import { Table } from '../Table';
import useLayout from '../../contexts/LayoutContext';
import { HydratedPosition } from '../../utils/data';
import useMarkets from '../../contexts/MarketsContext';
import { useUserTradeActivitiesQuery } from '../../graphql/generated';
import useWeb3 from '../../contexts/Web3Context';

const MobileContainer = styled.div`
  height: 100%;
  overflow: scroll;
`;

const DesktopContainer = styled.div`
  height: 100%;
  max-height: 60vh;
  overflow: scroll;
`;

export function Activity() {
  const { isMobile, isTablet } = useLayout();
  const { address } = useWeb3();
  const { marketList } = useMarkets();
  const { t } = useTranslation();

  const [{ data: userActivitiesData }] = useUserTradeActivitiesQuery({
    variables: {
      user: address?.toLowerCase() ?? '',
    },
    pause: !address,
  });

  const data = useMemo<ActivityInstance[]>(() => {
    if (marketList && userActivitiesData) {
      return (
        userActivitiesData.user?.activities.map((activity) => ({
          id: activity.id,
          market: marketList.find((market) => market.id === activity.market.id),
          action: activity.type,
          positionSize: activity.size,
          openNotional: activity.notional,
          price: activity.price,
          pnl: activity.pnl,
          fees: activity.fees,
          timestamp: Number(activity.timestamp * 1000),
        })) ?? []
      );
    }
    return [];
  }, [marketList, userActivitiesData]);

  const renderedRows = useMemo<Row[]>(() => {
    if (data && !isMobile && !isTablet) {
      return data.map((activity) => renderRow(activity, t));
    }
    return [];
  }, [data, isTablet, isMobile, t]);

  const renderedCondensedRows = useMemo<JSX.Element[] | null>(() => {
    if (data && (isMobile || isTablet)) {
      return data.map((activity) => renderCondensedRow(activity, t));
    }
    return null;
  }, [data, isTablet, isMobile, t]);

  if (!isMobile && !isTablet && renderedRows) {
    return (
      <DesktopContainer>
        <Table headerData={headers(t)} data={renderedRows} />
      </DesktopContainer>
    );
  }
  if (renderedCondensedRows) {
    return <MobileContainer>{renderedCondensedRows}</MobileContainer>;
  }
  return null;
}

Activity.defaultProps = {
  filter: (position: HydratedPosition) => position,
};
